import { useMemo } from 'react';
import { suggestionTaskRef } from '@shared/factory-suggestions';
import { parseTaskNumber } from '@shared/task-number';
import type { TaskRef } from '@shared/types/tasks';
import { useLoader } from '@/hooks/use-loader';
import { useCurrentProject } from '@/lib/current-project';
import { getTaskRefs } from '@/lib/tasks-api';
import type { SuggestionRound } from './task-suggestions';

/** The tasks the decisions made (Backlog, New branch) in these rounds, by number, in one request, so their refs link to them. */
export function useSuggestionTaskRefs(rounds: SuggestionRound[]): ReadonlyMap<number, TaskRef> | undefined {
  const projectId = useCurrentProject()?.id;
  const numbers = [
    ...new Set(
      // SF-T-308: and the tasks the AI builder reported making for Backlog picks.
      rounds.flatMap((r) => [...r.decided.values()].map((d) => (d.decision.outcome && 'task' in d.decision.outcome ? d.decision.outcome.task : suggestionTaskRef(d.decision)))).flatMap((ref) => {
        const number = ref ? parseTaskNumber(ref) : null;
        return number ? [number] : [];
      })
    ),
  ].sort((a, b) => a - b);
  const signature = numbers.join(',');
  const loader = useLoader((signal) => getTaskRefs(numbers, { signal }), [signature], {
    key: `${projectId}:task-refs:${signature}`,
    enabled: numbers.length > 0 && !!projectId,
    staleMs: 60_000,
  });
  const tasks = loader.data?.data?.tasks;
  return useMemo(() => (tasks ? new Map(tasks.map((t) => [t.number, t])) : undefined), [tasks]);
}
